import { supabase } from '@/lib/supabase/client';

export type BadgeKey = 'founder_city' | 'first_store' | 'first_post';

export interface BadgeDefinition {
  key: BadgeKey;
  title: string;
  description: string;
}

export interface UserBadge {
  id?: string;
  userId?: string;
  badgeKey: BadgeKey;
  badgeScope: string;
  city?: string;
  metadata?: Record<string, unknown>;
  awardedAt: string;
}

const BADGES_STORAGE_KEY = 'minibid_user_badges_local_v2';
const BADGES_REMOTE_DISABLED_KEY = 'minibid_user_badges_remote_disabled_v1';
let userBadgesTableAvailable: boolean | null = null;

const BADGE_DEFINITIONS: Record<BadgeKey, BadgeDefinition> = {
  founder_city: {
    key: 'founder_city',
    title: 'Fundador de ciudad',
    description: 'Creaste la primera tienda durante el desafio de una ciudad nueva.',
  },
  first_store: {
    key: 'first_store',
    title: 'Primera tienda',
    description: 'Creaste tu primera tienda en Minibid.',
  },
  first_post: {
    key: 'first_post',
    title: 'Primer post',
    description: 'Publicaste tu primer contenido en Minibid.',
  },
};

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function normalizeBadgeScope(value?: string | null): string {
  return normalizeText(value || '');
}

export function getBadgeDefinition(key: BadgeKey): BadgeDefinition {
  return BADGE_DEFINITIONS[key];
}

function readLocalBadges(): UserBadge[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(BADGES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as UserBadge[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalBadges(badges: UserBadge[]): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(BADGES_STORAGE_KEY, JSON.stringify(badges));
  } catch {
    // Ignore localStorage quota/private mode issues
  }
}

function mapDbBadge(row: any): UserBadge {
  return {
    id: row.id,
    userId: row.user_id,
    badgeKey: row.badge_key,
    badgeScope: row.badge_scope || '',
    city: row.city || undefined,
    metadata: row.metadata || {},
    awardedAt: row.awarded_at,
  };
}

function isMissingUserBadgesTableError(error: unknown): boolean {
  const code = (error as { code?: string } | null | undefined)?.code;
  const message = String((error as { message?: string } | null | undefined)?.message || '').toLowerCase();
  return code === 'PGRST205' || message.includes('user_badges') || message.includes("could not find the table 'public.user_badges'");
}

function markUserBadgesTableAvailability(error: unknown): void {
  if (isMissingUserBadgesTableError(error)) {
    userBadgesTableAvailable = false;
    if (isBrowser()) {
      try {
        window.localStorage.setItem(BADGES_REMOTE_DISABLED_KEY, '1');
      } catch {
        // Ignore storage write failures
      }
    }
  }
}

function shouldUseLocalBadgesOnly(): boolean {
  if (userBadgesTableAvailable === null && isBrowser()) {
    try {
      if (window.localStorage.getItem(BADGES_REMOTE_DISABLED_KEY) === '1') {
        userBadgesTableAvailable = false;
      }
    } catch {
      // Ignore storage read failures
    }
  }
  return userBadgesTableAvailable === false;
}

export async function listUserBadges(userId?: string | null): Promise<UserBadge[]> {
  if (!userId) return readLocalBadges();
  if (shouldUseLocalBadgesOnly()) return readLocalBadges();

  const { data, error } = await supabase
    .from('user_badges')
    .select('id, user_id, badge_key, badge_scope, city, metadata, awarded_at')
    .eq('user_id', userId)
    .order('awarded_at', { ascending: false });

  if (error) {
    markUserBadgesTableAvailability(error);
    if (!isMissingUserBadgesTableError(error)) {
      console.warn('[badges] listUserBadges fallback to local due to error:', error);
    }
    return readLocalBadges();
  }

  userBadgesTableAvailable = true;
  if (isBrowser()) {
    try {
      window.localStorage.removeItem(BADGES_REMOTE_DISABLED_KEY);
    } catch {
      // Ignore storage write failures
    }
  }

  return (data || []).map(mapDbBadge);
}

export async function hasBadge(params: {
  userId?: string | null;
  badgeKey: BadgeKey;
  badgeScope?: string | null;
}): Promise<boolean> {
  const scope = normalizeBadgeScope(params.badgeScope);

  if (!params.userId) {
    return readLocalBadges().some((badge) =>
      badge.badgeKey === params.badgeKey && normalizeBadgeScope(badge.badgeScope) === scope
    );
  }

  if (shouldUseLocalBadgesOnly()) {
    return readLocalBadges().some((badge) =>
      badge.badgeKey === params.badgeKey && normalizeBadgeScope(badge.badgeScope) === scope
    );
  }

  const { data, error } = await supabase
    .from('user_badges')
    .select('id')
    .eq('user_id', params.userId)
    .eq('badge_key', params.badgeKey)
    .eq('badge_scope', scope)
    .maybeSingle();

  if (error) {
    markUserBadgesTableAvailability(error);
    if (!isMissingUserBadgesTableError(error)) {
      console.warn('[badges] hasBadge fallback to local due to error:', error);
    }
    return readLocalBadges().some((badge) =>
      badge.badgeKey === params.badgeKey && normalizeBadgeScope(badge.badgeScope) === scope
    );
  }

  userBadgesTableAvailable = true;
  if (isBrowser()) {
    try {
      window.localStorage.removeItem(BADGES_REMOTE_DISABLED_KEY);
    } catch {
      // Ignore storage write failures
    }
  }

  return Boolean(data?.id);
}

export async function awardBadge(params: {
  userId?: string | null;
  badgeKey: BadgeKey;
  badgeScope?: string | null;
  city?: string | null;
  metadata?: Record<string, unknown>;
}): Promise<UserBadge | null> {
  const scope = normalizeBadgeScope(params.badgeScope);
  const metadata = params.metadata || {};

  if (!params.userId) {
    const localBadges = readLocalBadges();
    const exists = localBadges.some((badge) =>
      badge.badgeKey === params.badgeKey && normalizeBadgeScope(badge.badgeScope) === scope
    );
    if (exists) return null;

    const awarded: UserBadge = {
      badgeKey: params.badgeKey,
      badgeScope: scope,
      city: params.city || undefined,
      metadata,
      awardedAt: new Date().toISOString(),
    };

    writeLocalBadges([awarded, ...localBadges]);
    return awarded;
  }

  if (shouldUseLocalBadgesOnly()) {
    const localBadges = readLocalBadges();
    const existsLocal = localBadges.some((badge) =>
      badge.badgeKey === params.badgeKey && normalizeBadgeScope(badge.badgeScope) === scope
    );
    if (existsLocal) return null;

    const awarded: UserBadge = {
      badgeKey: params.badgeKey,
      badgeScope: scope,
      city: params.city || undefined,
      metadata,
      awardedAt: new Date().toISOString(),
    };
    writeLocalBadges([awarded, ...localBadges]);
    return awarded;
  }

  const existing = await hasBadge({
    userId: params.userId,
    badgeKey: params.badgeKey,
    badgeScope: scope,
  });
  if (existing) return null;

  const { data, error } = await supabase
    .from('user_badges')
    .insert({
      user_id: params.userId,
      badge_key: params.badgeKey,
      badge_scope: scope,
      city: params.city || null,
      metadata,
    })
    .select('id, user_id, badge_key, badge_scope, city, metadata, awarded_at')
    .single();

  if (error) {
    markUserBadgesTableAvailability(error);
    console.warn('[badges] awardBadge fallback to local due to error:', error);
    const localBadges = readLocalBadges();
    const existsLocal = localBadges.some((badge) =>
      badge.badgeKey === params.badgeKey && normalizeBadgeScope(badge.badgeScope) === scope
    );
    if (existsLocal) return null;

    const awarded: UserBadge = {
      badgeKey: params.badgeKey,
      badgeScope: scope,
      city: params.city || undefined,
      metadata,
      awardedAt: new Date().toISOString(),
    };
    writeLocalBadges([awarded, ...localBadges]);
    return awarded;
  }

  userBadgesTableAvailable = true;
  if (isBrowser()) {
    try {
      window.localStorage.removeItem(BADGES_REMOTE_DISABLED_KEY);
    } catch {
      // Ignore storage write failures
    }
  }

  return mapDbBadge(data);
}
